import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useIsInternalUser } from '@bingads-webui-campaign-react/basic-context-utils';
import { Stack, PrimaryButton, DefaultButton } from '@fluentui/react';
import {
  useWizardService,
  getIsFirstStep,
  getIsLastStep,
  getCurrentStepId,
  matchStepState,
  STEP_STATES,
  WIZARD_EVENTS,
} from '@bingads-webui-react/wizard';
import {
  Text,
  makeStyles,
  shorthands,
  tokens
} from '@fluentui/react-components';
import { CancelButton } from '@bingads-webui-react/wizard-ui-library';
import { MessageBar, MessageBarTitle, MessageBarBody } from '@fluentui/react-message-bar';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { STEP_PATHS, FLOWS } from '../step-constants';
import { useSkipAdCreationState } from '../common/use-skip-ad-creation';

const useStyles = makeStyles({
  warning: {
    ...shorthands.margin(tokens.spacingVerticalL, tokens.spacingVerticalNone),
  },
});


const SaveAndPublish = () => {
  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    if (activity) {
      activity.signal('onSaveAndPublishClicked');
    }
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isPublishing: true });
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [activity, send]);

  const buttonText = useMemo(() => {
    const currentStep = getCurrentStepId(state);

    if (currentStep === STEP_PATHS.SUMMARY) {
      return i18n.getString(_TL_('Go live'));
    }

    return i18n.getString(_TL_('Save & go live'));
  }, [i18n, state]);

  return (
    <PrimaryButton
      disabled={isBlocking}
      onClick={onNextClicked}
      className="step-save-and-publish-button"
    >
      {buttonText}
    </PrimaryButton>
  );
};

const SaveAsDraft = () => {
  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    if (activity) {
      activity.signal('onSaveAsDraftClicked');
    }
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isPublishing: false });
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [activity, send]);

  const buttonText = useMemo(() => {
    const currentStep = getCurrentStepId(state);

    if (currentStep === STEP_PATHS.SUMMARY) {
      return i18n.getString(_TL_('Don\'t go live'));
    }

    return i18n.getString(_TL_('Save & don\'t go live'));
  }, [i18n, state]);

  return (
    <DefaultButton
      disabled={isBlocking}
      onClick={onNextClicked}
      className="step-save-draft-button"
    >
      {buttonText}
    </DefaultButton>
  );
};

export const SaveAndExitButton = () => {
  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    if (activity) {
      activity.signal('onSaveAndExitButtonClicked');
    }
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isAdGroupPublishing: true });
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [activity, send]);

  return (
    <DefaultButton
      disabled={isBlocking}
      onClick={onNextClicked}
      className="step-save-and-exit-button"
    >
      {i18n.getString(_TL_('Save and exit'))}
    </DefaultButton>
  );
};

export const SkipAdCreationButton = () => {
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const { state, send } = useWizardService();
  const [, setSkipAdCreation] = useSkipAdCreationState();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING)
    || matchStepState(state, STEP_STATES.LOADING)
    || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    if (activity) {
      activity.signal('onSkipAdCreationButtonClicked');
    }
    send(WIZARD_EVENTS.SAVE_EMPTY_AND_NEXT);
    setSkipAdCreation(true);
  }, [activity, send, setSkipAdCreation]);

  return (
    <DefaultButton
      disabled={isBlocking}
      onClick={onNextClicked}
      className="step-skip-ad-creation-button"
    >
      {i18n.getString(_TL_('Proceed without ad'))}
    </DefaultButton>
  );
};

export const NextButton = ({ buttonText, disabled }) => {
  const i18n = useContext(I18nContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);
  const currentStepId = getCurrentStepId(state);

  const onNextClicked = useCallback(() => {
    if (currentStepId === STEP_PATHS.MULTI_AD_MANAGER) {
      send(WIZARD_EVENTS.NAVIGATE_TO, { stepId: STEP_PATHS.AD_GROUP });
    } else {
      send(WIZARD_EVENTS.SAVE_AND_NEXT);
    }
  }, [currentStepId, send]);

  return (
    <PrimaryButton
      disabled={disabled || isBlocking}
      onClick={onNextClicked}
      className="step-next-button"
    >
      {buttonText ? i18n.getString(buttonText) : i18n.getString(_TL_('Next'))}
    </PrimaryButton>
  );
};

export const BackButton = ({ buttonText }) => {
  const i18n = useContext(I18nContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);
  const currentStepId = getCurrentStepId(state);

  const onBackClicked = useCallback(() => {
    if (currentStepId === STEP_PATHS.AD_GROUP && state.context.wizardData.flow === FLOWS.MULTI_AUDIENCE_CAMPAIGNS) {
      send(WIZARD_EVENTS.NAVIGATE_TO, { stepId: STEP_PATHS.MULTI_AD_MANAGER });
    } else {
      send(WIZARD_EVENTS.BACK);
    }
  }, [currentStepId, send, state.context.wizardData.flow]);

  return (
    <DefaultButton
      disabled={isBlocking}
      onClick={onBackClicked}
      className="step-back-button"
    >
      {buttonText ? i18n.getString(buttonText) : i18n.getString(_TL_('Back'))}
    </DefaultButton>
  );
};
BackButton.propTypes = {
  buttonText: PropTypes.string,
};
BackButton.defaultProps = {
  buttonText: undefined,
};


NextButton.propTypes = {
  buttonText: PropTypes.string,
  disabled: PropTypes.bool,
};
NextButton.defaultProps = {
  buttonText: undefined,
  disabled: false,
};

export const DisplayPlusStepFooter = ({
  nextButtonText,
  nextDisabled,
  backButtonText,
  cancelButtonText,
  hideBackButton,
}) => {
  const { state } = useWizardService();
  const { dynamic: { AWFWebExclusionsWarning: showAWFWarning } } = useContext(PermissionsContext);
  const i18n = useContext(I18nContext);
  const styles = useStyles();
  const isFirstStep = getIsFirstStep(state);
  const isLastStep = getIsLastStep(state);

  const currentStep = getCurrentStepId(state);

  const isInternalUser = useIsInternalUser();
  const wasAdAlreadyCreated = state.context.wizardData.ads;

  return (
    <>
      {showAWFWarning && isLastStep && (
        <Stack>
          <div className={styles.warning}>
            <MessageBar layout="multiline">
              <MessageBarBody>
                <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
                <Text size={200}>{i18n.getString(_TL_('Please note that Audience campaigns will always serve on Microsoft sites (e.g., Microsoft Bing, Microsoft Casual Games, MSN, and Outlook.com) even if you’ve added website exclusions for them.'))}</Text>
              </MessageBarBody>
            </MessageBar>
          </div>
        </Stack>
      )}
      <Stack horizontal tokens={{ childrenGap: 16 }}>
        {(isFirstStep && isInternalUser && !wasAdAlreadyCreated) && <SkipAdCreationButton />}
        {(!isFirstStep && !hideBackButton) && <BackButton buttonText={backButtonText} />}
        {(!isLastStep ? <NextButton buttonText={nextButtonText} disabled={nextDisabled} /> : <SaveAndPublish />)}
        {isLastStep && <SaveAsDraft />}
        {currentStep === STEP_PATHS.AD_GROUP && <SaveAndExitButton />}
        {isFirstStep && <CancelButton buttonText={cancelButtonText} />}
      </Stack>
    </>
  );
};
DisplayPlusStepFooter.propTypes = {
  nextButtonText: PropTypes.string,
  nextDisabled: PropTypes.bool,
  backButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  hideBackButton: PropTypes.bool,
};
DisplayPlusStepFooter.defaultProps = {
  nextButtonText: undefined,
  nextDisabled: false,
  backButtonText: undefined,
  cancelButtonText: undefined,
  hideBackButton: false,
};
